<script setup lang="ts">
// Debug tab — realtime view onto kmb-stt, the on-device offline speech recognition service
// (apps/side/pingpong/kmb-stt/ — runs on the kiosk at /home/kombo/kmb-stt, pushed via scp; wiped on reboot until baked into the image). Connects
// directly to ws://127.0.0.1:9224 — a service running on the SAME device as the kiosk
// Chromium, not through the pingpong API — so this only lights up on the actual kiosk once
// kmb-stt is installed there; elsewhere it just shows "connecting…" forever, which is fine.
//
// This doubles as the tuning surface: seeing exactly what the recognizer hears (raw partial/
// final transcript + classified commands + confidence) is what lets the grammar/vocabulary get
// tuned for real score-calling. The language selector below sends a {type:"config"} control
// message that kmb-stt persists server-side — see kmb_stt.py's SttService.set_lang.
import { onMounted, onUnmounted, ref } from 'vue'
import { settings } from '../store/settings'
import { getPlayer, getVoiceProfiles } from '../backend'
import { sttTranscriptLog, clearTranscriptLog } from '../stt'
import { rankSpeakerCandidates, rankedSpeakerConfidences, CONFIDENCE_ACCEPT_THRESHOLD, type RankedSpeakerCandidate, type SpeakerCandidate } from '../voiceScore'
// Lightweight, no @xenova/transformers dependency (see semanticState.ts's docblock) — the actual
// model-loading module is dynamically imported below, only when "Load now" is pressed, so this
// panel never pulls the heavy embedding library into DebugStt.vue's (statically-imported, per
// App.vue) chunk.
import { semanticDebug, MIN_SIMILARITY, MIN_MARGIN } from '../semanticState'

function loadSemanticModelNow() {
  void import('../semanticIntent').then((m) => m.ensureSemanticModelLoaded())
}
import type { Player, VoiceProfile } from '../types'

type Lang = 'es' | 'en'
type LangSetting = Lang | 'both'

interface StatusEvent {
  type: 'status'
  lang: LangSetting
  activeLangs: Lang[]
  modelsLoaded: Lang[]
  uptime: number
  ts: number
}
interface LevelEvent { type: 'level'; rms: number; ts: number }
interface PartialEvent { type: 'partial'; text: string; lang: Lang; ts: number }
interface FinalEvent { type: 'final'; text: string; lang: Lang; spk?: number[]; spkModelVersion?: string; durationSec?: number | null; ts: number }
interface CommandEvent {
  type: 'command'
  command: 'start' | 'end' | 'score'
  raw: string
  lang: Lang
  confidence: number
  score: { left: number; right: number } | null
  ts: number
}
interface ErrorEvent { type: 'error'; message: string }
type SttEvent = StatusEvent | LevelEvent | PartialEvent | FinalEvent | CommandEvent | ErrorEvent

const WS_URL = 'ws://127.0.0.1:9224'
const BACKOFF_START_MS = 1000
const BACKOFF_MAX_MS = 10000

const connectionState = ref<'connecting' | 'open' | 'closed'>('connecting')
const status = ref<StatusEvent | null>(null)
const rms = ref(0)
const lastCommand = ref<CommandEvent | null>(null)
const commandFlash = ref(false)

// Round-25 (2026-08-31): the transcript pane below used to be fed only by THIS component's own
// WS connection (so it only showed history from whatever moment the Debug tab happened to be
// open). It now renders the shared app-wide log (stt.ts's sttTranscriptLog) instead — fed by the
// ONE app-wide kmb-stt client regardless of which screen is mounted, so a claim said on Home
// shows up here too. This component's own connection (below) is kept for everything that needs a
// live per-tab feed: the VU meter, the language selector's config frame, the commands strip, and
// the speaker-match panel (matchSpeaker still runs off this connection's 'final' events).
function copyTranscriptLog() {
  void navigator.clipboard?.writeText(JSON.stringify(sttTranscriptLog, null, 2)).catch(() => {
    /* clipboard unavailable/denied — best effort, no UI feedback needed for a debug convenience */
  })
}

// Round-19 (2026-08-31): "Speaker match" panel — full ranked candidate list against ALL
// enrolled profile centroids for every final carrying an x-vector, not just the single best
// match (there's no match context here, this is a raw tuning surface). Supersedes the old
// single-name "Voice match" strip from round-16.
// confidence is null for a skippedModelMismatch candidate (not comparable, excluded from the
// calibration entirely — see rankedSpeakerConfidences).
interface NamedCandidate extends RankedSpeakerCandidate { name: string; confidence: number | null }
interface SpeakerMatchState {
  utteranceText: string
  utteranceLang: Lang
  utteranceDurationSec: number | null | undefined
  candidates: NamedCandidate[] // empty when no profiles enrolled
}
const speakerMatch = ref<SpeakerMatchState | null>(null) // null = last final had no x-vector (too short)
let voiceProfiles: VoiceProfile[] = []
let profilesLoaded = false
async function ensureVoiceProfiles() {
  if (profilesLoaded) return
  profilesLoaded = true
  try {
    voiceProfiles = (await getVoiceProfiles()).items.filter((p) => p.centroid && p.centroid.length > 0)
  } catch {
    voiceProfiles = []
  }
}
const playerNameCache = new Map<number, string>()
async function nameFor(playerId: number): Promise<string> {
  const cached = playerNameCache.get(playerId)
  if (cached) return cached
  try {
    const p: Player = await getPlayer(playerId)
    playerNameCache.set(playerId, p.displayName)
    return p.displayName
  } catch {
    return `#${playerId}`
  }
}
async function matchSpeaker(final: FinalEvent) {
  if (!final.spk) {
    speakerMatch.value = null
    return
  }
  await ensureVoiceProfiles()
  const candidates: SpeakerCandidate[] = voiceProfiles.map((p) => ({ id: p.playerId, centroid: p.centroid!, spkModelVersion: p.spkModelVersion }))
  const ranked = rankSpeakerCandidates(final.spk, final.spkModelVersion, candidates)
  const confidenceById = new Map(rankedSpeakerConfidences(ranked, final.durationSec).map((c) => [c.id, c.confidence]))
  const named: NamedCandidate[] = await Promise.all(
    ranked.map(async (c) => ({ ...c, name: await nameFor(c.id), confidence: confidenceById.get(c.id) ?? null })),
  )
  speakerMatch.value = { utteranceText: final.text, utteranceLang: final.lang, utteranceDurationSec: final.durationSec, candidates: named }
}

let ws: WebSocket | null = null
let closedByUs = false
let backoff = BACKOFF_START_MS
let retryTimer: ReturnType<typeof setTimeout> | null = null
let flashTimer: ReturnType<typeof setTimeout> | null = null

function handleEvent(ev: SttEvent) {
  switch (ev.type) {
    case 'status':
      status.value = ev
      break
    case 'level':
      rms.value = ev.rms
      break
    case 'partial':
      // No local line-tracking anymore — the transcript pane reads the shared app-wide log
      // (stt.ts's sttTranscriptLog), fed by the OTHER (shared) kmb-stt connection. This
      // component's own 'partial' events aren't otherwise needed.
      break
    case 'final':
      void matchSpeaker(ev)
      break
    case 'command':
      lastCommand.value = ev
      commandFlash.value = true
      if (flashTimer) clearTimeout(flashTimer)
      flashTimer = setTimeout(() => { commandFlash.value = false }, 2500)
      break
    case 'error':
      // eslint-disable-next-line no-console
      console.warn('[kmb-stt] error event:', ev.message)
      break
  }
}

function connect() {
  if (closedByUs) return
  connectionState.value = 'connecting'
  ws = new WebSocket(WS_URL)

  ws.addEventListener('open', () => {
    backoff = BACKOFF_START_MS
    connectionState.value = 'open'
  })

  ws.addEventListener('message', (evt) => {
    try {
      handleEvent(JSON.parse(evt.data as string) as SttEvent)
    } catch {
      // malformed frame — ignore, next one self-corrects
    }
  })

  ws.addEventListener('close', () => {
    connectionState.value = 'closed'
    ws = null
    if (closedByUs) return
    retryTimer = setTimeout(connect, backoff)
    backoff = Math.min(backoff * 2, BACKOFF_MAX_MS)
  })

  ws.addEventListener('error', () => {
    ws?.close()
  })
}

function setLang(lang: LangSetting) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify({ type: 'config', lang }))
  }
}

// Round-8 feedback item 23: the Settings "Voice commands" toggle gates the app's HANDLING of
// STT events — this debug feed is the only place that currently consumes them, so turning the
// toggle off simply skips connecting at all rather than connecting-but-ignoring (no half-state).
onMounted(() => {
  if (settings.voiceCommandsEnabled) connect()
})
onUnmounted(() => {
  closedByUs = true
  if (retryTimer) clearTimeout(retryTimer)
  if (flashTimer) clearTimeout(flashTimer)
  ws?.close()
})

const langLabel: Record<LangSetting, string> = { es: 'ES', en: 'EN', both: 'Both' }
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col p-4 gap-3">
    <div class="flex items-center gap-3">
      <h1 class="text-xl font-bold flex-1">Debug — speech recognition</h1>
      <div
        v-if="settings.voiceCommandsEnabled"
        class="px-3 py-1 rounded-full text-xs font-semibold"
        :class="{
          'bg-emerald-600 text-white': connectionState === 'open',
          'bg-amber-600 text-white': connectionState === 'connecting',
          'bg-red-700 text-white': connectionState === 'closed',
        }"
      >
        {{ connectionState === 'open' ? 'connected' : connectionState === 'connecting' ? 'connecting…' : 'disconnected' }}
      </div>
    </div>

    <p v-if="!settings.voiceCommandsEnabled" class="text-neutral-500">
      Voice commands are turned off in Settings — enable them there to connect to kmb-stt.
    </p>
    <template v-else>

    <div class="flex items-center gap-4 text-xs text-neutral-400 font-mono">
      <span v-if="status">lang={{ status.lang }} active={{ status.activeLangs.join(',') }} models={{ status.modelsLoaded.join(',') }} uptime={{ status.uptime.toFixed(0) }}s</span>
      <span v-else>no status yet</span>
    </div>

    <div class="flex items-center gap-3">
      <span class="text-xs text-neutral-500 font-semibold shrink-0">Language</span>
      <div class="flex gap-2">
        <button
          v-for="opt in (['es', 'en', 'both'] as LangSetting[])"
          :key="opt"
          type="button"
          class="tap-target px-4 rounded-xl font-semibold text-sm"
          :class="status?.lang === opt ? 'bg-emerald-600' : 'bg-neutral-800 active:bg-neutral-700'"
          @click="setLang(opt)"
        >
          {{ langLabel[opt] }}
        </button>
      </div>
    </div>

    <!-- VU meter -->
    <div class="flex items-center gap-3">
      <span class="text-xs text-neutral-500 font-semibold shrink-0 w-10">Level</span>
      <div class="flex-1 h-3 rounded-full bg-neutral-800 overflow-hidden">
        <div
          class="h-full bg-emerald-500 transition-all duration-75"
          :style="{ width: `${Math.min(100, rms * 300)}%` }"
        />
      </div>
      <span class="text-xs text-neutral-500 font-mono w-14 text-right">{{ rms.toFixed(3) }}</span>
    </div>

    <!-- Commands strip -->
    <div
      class="rounded-xl px-4 py-3 flex items-center gap-3 transition-colors duration-300"
      :class="commandFlash ? 'bg-emerald-600' : 'bg-neutral-900 border border-neutral-800'"
    >
      <template v-if="lastCommand">
        <span class="text-lg font-bold uppercase">{{ lastCommand.command }}</span>
        <span v-if="lastCommand.score" class="text-2xl font-black tabular-nums">
          {{ lastCommand.score.left }}&ndash;{{ lastCommand.score.right }}
        </span>
        <span class="text-xs opacity-80 flex-1 truncate">raw: "{{ lastCommand.raw }}" ({{ lastCommand.lang }})</span>
        <span class="text-xs font-mono opacity-80 shrink-0">conf {{ (lastCommand.confidence * 100).toFixed(0) }}%</span>
      </template>
      <template v-else>
        <span class="text-sm text-neutral-500">No command recognized yet — try "empezar"/"start", or a score like "tres dos"/"three two".</span>
      </template>
    </div>

    <!-- Round-19 (2026-08-31): Speaker match panel — ranked candidates against every enrolled
         profile centroid for the most recent final that carried an x-vector. Supersedes the
         round-16 single-name "Voice match" strip. -->
    <div class="rounded-xl px-4 py-3 flex flex-col gap-2 bg-neutral-900 border border-neutral-800 text-sm">
      <div class="flex items-center gap-2">
        <span class="text-xs font-bold text-neutral-500 uppercase tracking-wide shrink-0">Speaker match</span>
        <span v-if="speakerMatch" class="text-xs text-neutral-500 flex-1 truncate font-mono">
          [{{ speakerMatch.utteranceLang }}] "{{ speakerMatch.utteranceText || '(empty)' }}"
          <span v-if="speakerMatch.utteranceDurationSec != null">&middot; {{ speakerMatch.utteranceDurationSec.toFixed(2) }}s</span>
          <span v-else>&middot; duration n/a</span>
        </span>
      </div>

      <p v-if="!speakerMatch" class="text-neutral-600">
        No x-vector on the last final (utterance too short) — nothing to match yet.
      </p>
      <p v-else-if="speakerMatch.candidates.length === 0" class="text-neutral-600">
        No voice profiles enrolled — nothing to match "{{ speakerMatch.utteranceText || '(empty)' }}" against.
      </p>
      <div v-else class="flex flex-col gap-1.5">
        <!-- Round-20 (2026-08-31): calibrated confidence (voiceScore.ts speakerConfidence(),
             softmax + duration-quality) is the PRIMARY number now — raw cosine similarity from
             round-19 stays visible as a small secondary value for debugging, never the headline. -->
        <div
          v-for="(c, i) in speakerMatch.candidates"
          :key="c.id"
          class="flex items-center gap-2"
        >
          <span class="w-32 truncate shrink-0" :class="i === 0 && c.confidence != null && c.confidence >= CONFIDENCE_ACCEPT_THRESHOLD ? 'font-bold text-emerald-400' : 'text-neutral-300'">{{ c.name }}</span>
          <div class="flex-1 h-2 rounded-full bg-neutral-800 overflow-hidden">
            <div
              class="h-full transition-all duration-150"
              :class="c.skippedModelMismatch ? 'bg-neutral-700' : i === 0 && c.confidence != null && c.confidence >= CONFIDENCE_ACCEPT_THRESHOLD ? 'bg-emerald-500' : 'bg-neutral-500'"
              :style="{ width: `${Math.max(0, Math.min(100, (c.confidence ?? 0) * 100))}%` }"
            />
          </div>
          <span
            class="font-mono font-bold w-14 text-right shrink-0"
            :class="c.skippedModelMismatch ? 'text-neutral-600' : i === 0 && c.confidence != null && c.confidence >= CONFIDENCE_ACCEPT_THRESHOLD ? 'text-emerald-400' : 'text-neutral-500'"
          >
            {{ c.skippedModelMismatch || c.confidence == null ? 'n/a' : `${Math.round(c.confidence * 100)}%` }}
          </span>
          <span class="font-mono text-[10px] text-neutral-600 w-16 text-right shrink-0">
            {{ c.skippedModelMismatch ? '' : `cos ${c.similarity.toFixed(2)}` }}
          </span>
          <span v-if="c.skippedModelMismatch" class="text-[10px] text-amber-500 shrink-0 uppercase tracking-wide">model mismatch</span>
        </div>
      </div>
    </div>

    <!-- Round-24 (2026-08-31): Tier-2 semantic intent fallback panel (DESIGN.md §11 "Semantic
         voice matching", src/semanticIntent.ts) — model load state + the last transcript's
         ranked intent scores, so tuning semanticExemplars.ts can be validated live against real
         speech instead of only via scripts/semantic-sanity.mjs. -->
    <div class="rounded-xl px-4 py-3 flex flex-col gap-2 bg-neutral-900 border border-neutral-800 text-sm">
      <div class="flex items-center gap-2">
        <span class="text-xs font-bold text-neutral-500 uppercase tracking-wide shrink-0">Semantic fallback</span>
        <span
          class="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide"
          :class="{
            'bg-neutral-700 text-neutral-300': semanticDebug.modelState === 'idle',
            'bg-amber-600 text-white': semanticDebug.modelState === 'loading',
            'bg-emerald-600 text-white': semanticDebug.modelState === 'ready',
            'bg-red-700 text-white': semanticDebug.modelState === 'error',
          }"
        >
          {{ semanticDebug.modelState }}
        </span>
        <span v-if="!settings.semanticFallbackEnabled" class="text-xs text-neutral-600">disabled in Settings</span>
        <span v-else-if="semanticDebug.modelState === 'ready'" class="text-xs text-neutral-600 font-mono">loaded in {{ semanticDebug.modelLoadMs }}ms</span>
        <span v-else-if="semanticDebug.modelState === 'error'" class="text-xs text-red-400 truncate">{{ semanticDebug.modelError }}</span>
        <button
          v-if="semanticDebug.modelState === 'idle' || semanticDebug.modelState === 'error'"
          type="button"
          class="ml-auto tap-target px-3 rounded-lg bg-neutral-800 active:bg-neutral-700 text-xs font-semibold shrink-0"
          @click="loadSemanticModelNow()"
        >
          Load now
        </button>
      </div>

      <p class="text-[10px] text-neutral-600">
        Accept gate: top &ge; {{ MIN_SIMILARITY.toFixed(2) }} AND margin over runner-up &ge; {{ MIN_MARGIN.toFixed(2) }} — fires only on utterances the grammar path left unclassified.
      </p>

      <p v-if="!semanticDebug.lastClassification" class="text-neutral-600">No semantic classification yet.</p>
      <div v-else class="flex flex-col gap-1.5">
        <div class="text-xs text-neutral-500 font-mono truncate">
          "{{ semanticDebug.lastClassification.transcript }}" &middot; {{ semanticDebug.lastClassification.latencyMs }}ms
          &middot; margin {{ semanticDebug.lastClassification.margin.toFixed(3) }}
          &middot;
          <span :class="semanticDebug.lastClassification.accepted ? 'text-emerald-400 font-bold' : 'text-neutral-500'">
            {{ semanticDebug.lastClassification.accepted ? 'ACCEPTED' : `rejected (${semanticDebug.lastClassification.rejectReason})` }}
          </span>
        </div>
        <div
          v-for="(r, i) in semanticDebug.lastClassification.ranked.slice(0, 5)"
          :key="r.intent"
          class="flex items-center gap-2"
        >
          <span class="w-40 truncate shrink-0" :class="i === 0 && semanticDebug.lastClassification.accepted ? 'font-bold text-emerald-400' : 'text-neutral-300'">{{ r.intent }}</span>
          <div class="flex-1 h-2 rounded-full bg-neutral-800 overflow-hidden">
            <div
              class="h-full transition-all duration-150"
              :class="i === 0 && semanticDebug.lastClassification.accepted ? 'bg-emerald-500' : 'bg-neutral-500'"
              :style="{ width: `${Math.max(0, Math.min(100, r.score * 100))}%` }"
            />
          </div>
          <span class="font-mono font-bold w-14 text-right shrink-0 text-neutral-400">{{ r.score.toFixed(3) }}</span>
        </div>
      </div>
    </div>

    <!-- Transcript — round-25 (2026-08-31): app-wide history (stt.ts's sttTranscriptLog), not
         just what arrived while this tab was open. See handleEvent's 'partial'/'final' cases
         above for why this component's own connection no longer feeds it. -->
    <div class="flex items-center gap-2">
      <span class="text-xs font-bold text-neutral-500 uppercase tracking-wide">Transcript (app-wide)</span>
      <span class="text-[10px] text-neutral-600">{{ sttTranscriptLog.length }} entries, last 200 kept</span>
      <div class="ml-auto flex gap-2">
        <button type="button" class="tap-target px-3 rounded-lg bg-neutral-800 active:bg-neutral-700 text-xs font-semibold" @click="copyTranscriptLog">
          Copy
        </button>
        <button type="button" class="tap-target px-3 rounded-lg bg-neutral-800 active:bg-neutral-700 text-xs font-semibold" @click="clearTranscriptLog">
          Clear
        </button>
      </div>
    </div>
    <div class="flex-1 min-h-0 rounded-xl bg-neutral-900 border border-neutral-800 p-3 overflow-y-auto flex flex-col gap-1 font-mono text-sm">
      <div v-if="sttTranscriptLog.length === 0" class="text-neutral-600">Waiting for speech…</div>
      <div
        v-for="entry in sttTranscriptLog"
        :key="entry.id"
        class="flex flex-wrap items-baseline gap-x-2"
        :class="entry.kind === 'final' ? 'text-neutral-100' : 'text-neutral-600 italic'"
      >
        <span class="text-neutral-600 text-xs shrink-0">{{ entry.time }}</span>
        <span class="text-neutral-600 shrink-0">[{{ entry.lang }}]</span>
        <span>{{ entry.text || '(empty)' }}</span>
        <span v-if="entry.durationSec != null" class="text-[10px] text-neutral-600 shrink-0">{{ entry.durationSec.toFixed(2) }}s</span>
        <span
          v-if="entry.command"
          class="text-[10px] px-1.5 py-0.5 rounded-full font-sans font-semibold shrink-0"
          :class="entry.command.source === 'semantic' ? 'bg-amber-700 text-white' : 'bg-emerald-700 text-white'"
        >
          {{ entry.command.command }}{{ entry.command.color ? `/${entry.command.color}` : '' }} &middot; {{ entry.command.source }} &middot; {{ (entry.command.confidence * 100).toFixed(0) }}%
        </span>
        <span v-if="entry.speakerMatch" class="text-[10px] px-1.5 py-0.5 rounded-full font-sans font-semibold shrink-0 bg-neutral-700 text-neutral-200">
          spk: {{ entry.speakerMatch.displayName ?? (entry.speakerMatch.playerId != null ? `#${entry.speakerMatch.playerId}` : '?') }}
          <template v-if="entry.speakerMatch.confidencePct != null"> {{ entry.speakerMatch.confidencePct }}%</template>
          ({{ entry.speakerMatch.outcome }})
        </span>
        <!-- Round-28 (2026-08-31): which candidate-scope tier (services/candidateScope.ts) this
             match attempt was resolved against — "why didn't it consider player X" is answered
             right here instead of re-deriving it from the screen/context. -->
        <span v-if="entry.speakerMatch?.scope" class="text-[10px] px-1.5 py-0.5 rounded-full font-sans font-semibold shrink-0 bg-sky-900 text-sky-200">
          scope: {{ entry.speakerMatch.scope }}
        </span>
      </div>
    </div>
    </template>
  </div>
</template>
